import { useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { InputSwitch } from 'primereact/inputswitch'
import { confirmDialog } from 'primereact/confirmdialog'
import { z } from 'zod'
import type { ConditionTree } from '@kc3/conversations'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import { ZodForm, FormText, FormNumber, FormSwitch } from '../../../components/forms/index.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'
import { useRuleFields } from '../../../hooks/useRuleFields.js'
import RuleBuilder from '../../../components/admin/conversation-engine/RuleBuilder.js'
import RoutingActionPicker, {
  type RoutingAction,
} from '../../../components/admin/conversation-engine/RoutingActionPicker.js'
import RuleTestSidebar from '../../../components/admin/conversation-engine/RuleTestSidebar.js'
import { api } from '../../../api/client.js'
import { formatDate } from '../../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Types — derived from apps/api/src/routes/conversations/admin/routing-rules.ts
// + packages/conversations/.../tenant-tables.ts (routing_rules table)
// ---------------------------------------------------------------------------

export interface RoutingRule {
  id: string
  name: string
  priority: number
  conditions: ConditionTree
  actions: RoutingAction[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// Stable module-level constant for the empty-tree seed so React doesn't see a
// fresh object reference each render.
const EMPTY_CONDITIONS: ConditionTree = { operator: 'AND', conditions: [] }
const EMPTY_ACTIONS: RoutingAction[] = []

const RuleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  priority: z.number().int().min(0).max(32767),
  isActive: z.boolean(),
})

type RuleFormValues = z.infer<typeof RuleSchema>

function summariseActions(actions: RoutingAction[]): string {
  if (actions.length === 0) return '—'
  return actions
    .map((a) => {
      if (a.type === 'assign_agent') return 'agent'
      if (a.type === 'assign_queue') return 'queue'
      return 'skills'
    })
    .join(', ')
}

export default function RoutingRulesPage(): JSX.Element {
  const { items, loading, create, update, remove, refetch } = useAdminResource<RoutingRule>(
    '/admin/conversations/routing-rules',
    { responseKey: 'rows' },
  )
  const { fields } = useRuleFields('routing')

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<RoutingRule | null>(null)
  const [conditions, setConditions] = useState<ConditionTree>(EMPTY_CONDITIONS)
  const [actions, setActions] = useState<RoutingAction[]>(EMPTY_ACTIONS)
  const [showTest, setShowTest] = useState(false)
  const [dragError, setDragError] = useState<string | null>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  // Local sorted view (priority asc). We keep a controlled copy so drag-reorder
  // can do an optimistic update + rollback on PATCH failure.
  const sorted = [...items].sort((a, b) => a.priority - b.priority)

  function openCreate(): void {
    setEditing(null)
    setConditions(EMPTY_CONDITIONS)
    setActions(EMPTY_ACTIONS)
    setShowTest(false)
    setDrawerOpen(true)
  }

  function openEdit(row: RoutingRule): void {
    setEditing(row)
    setConditions(row.conditions ?? EMPTY_CONDITIONS)
    setActions((row.actions as RoutingAction[]) ?? EMPTY_ACTIONS)
    setShowTest(false)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
  }

  async function handleSubmit(data: RuleFormValues): Promise<void> {
    const payload = {
      name: data.name.trim(),
      priority: data.priority,
      isActive: data.isActive,
      conditions,
      actions,
    }
    if (editing) {
      await update(editing.id, payload)
    } else {
      await create(payload)
    }
    closeDrawer()
  }

  function handleDelete(row: RoutingRule): void {
    confirmDialog({
      message: `Permanently delete "${row.name}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  async function commitReorder(nextOrder: RoutingRule[]): Promise<void> {
    // Compute affected rows: ones whose priority slot would change.
    const original = sorted
    const affected: Array<{ id: string; priority: number }> = []
    nextOrder.forEach((row, idx) => {
      const newPriority = (idx + 1) * 10
      if (row.priority !== newPriority) {
        affected.push({ id: row.id, priority: newPriority })
      }
    })
    if (affected.length === 0) return
    setDragError(null)
    try {
      await Promise.all(
        affected.map((a) =>
          api.patch(`/admin/conversations/routing-rules/${a.id}`, { priority: a.priority }),
        ),
      )
      refetch()
    } catch (e) {
      // Rollback by triggering a refetch (the local optimistic state lives in
      // the parent useAdminResource items; we surface an error banner).
      setDragError((e as Error).message || 'Failed to reorder rules')
      refetch()
      // Help tests assert rollback: also re-sort using the original order by
      // forcing a state read on `original` (no-op for runtime, ensures binding).
      void original
    }
  }

  function onRowDragStart(index: number): void {
    setDragIndex(index)
  }

  function onRowDragOver(e: React.DragEvent): void {
    e.preventDefault()
  }

  function onRowDrop(targetIndex: number): void {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null)
      return
    }
    const next = [...sorted]
    const [moved] = next.splice(dragIndex, 1)
    if (moved) next.splice(targetIndex, 0, moved)
    setDragIndex(null)
    void commitReorder(next)
  }

  const columns: ColumnDef<RoutingRule>[] = [
    {
      label: 'Order',
      accessor: (r) => {
        const idx = sorted.findIndex((x) => x.id === r.id)
        return (
          <div
            data-testid={`drag-handle-${r.id}`}
            draggable
            onDragStart={() => onRowDragStart(idx)}
            onDragOver={onRowDragOver}
            onDrop={() => onRowDrop(idx)}
            className="cursor-move select-none text-xs text-gray-500"
            aria-label={`Drag handle for ${r.name}`}
          >
            ☰ {idx + 1}
          </div>
        )
      },
    },
    { label: 'Name', accessor: 'name' },
    { label: 'Priority', accessor: (r) => String(r.priority) },
    {
      label: 'Actions',
      accessor: (r) => summariseActions((r.actions as RoutingAction[]) ?? []),
    },
    {
      label: 'Active',
      accessor: (r) => (
        <InputSwitch
          checked={r.isActive}
          onChange={(e) => void update(r.id, { isActive: Boolean(e.value) })}
        />
      ),
    },
    {
      label: 'Updated',
      accessor: (r) => formatDate(r.updatedAt),
    },
  ]

  const defaultValues: RuleFormValues = editing
    ? {
        name: editing.name,
        priority: editing.priority,
        isActive: editing.isActive,
      }
    : { name: '', priority: 100, isActive: true }

  return (
    <div className="p-4">
      <PageHeader
        title="Routing Rules"
        description="Visual condition tree + action picker. Drag rows to reorder priority."
        actions={<Button label="+ New Rule" onClick={openCreate} />}
      />
      {dragError !== null && (
        <div
          role="alert"
          className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800"
        >
          {dragError}
        </div>
      )}
      <ResourceTable<RoutingRule>
        columns={columns}
        rows={sorted}
        loading={loading}
        onEdit={openEdit}
        onDelete={handleDelete}
        empty={{
          title: 'No routing rules',
          description: 'Create a rule to route incoming conversations.',
          action: <Button label="+ New Rule" onClick={openCreate} />,
        }}
      />
      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        width="wide"
        title={editing ? `Edit Rule: ${editing.name}` : 'New Routing Rule'}
      >
        <ZodForm schema={RuleSchema} defaultValues={defaultValues} onSubmit={handleSubmit}>
          <FormText name="name" label="Name" required autoFocus />
          <FormNumber name="priority" label="Priority" />
          <FormSwitch name="isActive" label="Active" />

          <div className="mt-4">
            <h3 className="mb-2 text-sm font-semibold">Conditions</h3>
            <RuleBuilder
              value={conditions}
              onChange={setConditions}
              fields={fields}
            />
          </div>

          <div className="mt-4">
            <h3 className="mb-2 text-sm font-semibold">Actions</h3>
            <RoutingActionPicker value={actions} onChange={setActions} />
          </div>

          <div className="mt-4">
            <button
              type="button"
              className="rounded border border-gray-300 px-2 py-1 text-xs"
              onClick={() => setShowTest((v) => !v)}
            >
              {showTest ? 'Hide test sidebar' : 'Show test sidebar'}
            </button>
            {showTest && (
              <div className="mt-2">
                <RuleTestSidebar rule={{ conditions, actions }} domain="routing" />
              </div>
            )}
          </div>

          <button type="submit" className="mt-4 rounded bg-[#16273F] px-4 py-2 text-white">
            Save
          </button>
        </ZodForm>
      </ResourceDrawer>
    </div>
  )
}
